/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * TypeScript/React version of FsiExtImg
 * Based on the original JSX version in /lego/page/browser/doc-icon/FsiExtImg.jsx
 */

import React from 'react'
import { FsiType } from '../../constants/fsi'
import { getAssetPath } from '../../utils/assetPath'

interface FileSystemItem {
  id: string | number
  name?: string
  type?: string
  valid?: boolean
  systemFolderType?: string
  acl?: any
  isFilePlan?: boolean
  showWorkflowIcon?: boolean
  inWorkflow?: boolean
  maxDocuments?: number
  workflow?: {
    id: string | number
    [key: string]: any
  }
  [key: string]: any // Allow additional properties
}

interface FsiExtImgProps {
  fsi: FileSystemItem
  inWorkflow?: boolean
}

export const extensionFilter = (input?: string): string => {
  const supportedExts = [
    'doc',
    'docx',
    'xls',
    'xlsx',
    'txt',
    'ai',
    'psd',
    'eps',
    'jpg',
    'jpeg',
    'pdf',
    'png',
    'ppt',
    'pptx'
  ]
  const ext = input ? String(input.split('.').pop()).toLowerCase() : null

  if (!ext || supportedExts.indexOf(ext) === -1) {
    return 'default'
  } else {
    return ext
  }
}

const FsiExtImg: React.FC<FsiExtImgProps> = ({ fsi, inWorkflow = false }) => {
  // Determine if workflow badge should be shown - matches JSX logic exactly
  const hasWfBadge = inWorkflow || (fsi.showWorkflowIcon && fsi.inWorkflow) || (fsi.workflow && fsi.workflow.id)

  const getIconPath = (): string => {
    // File plan handling
    if (fsi.isFilePlan) {
      return getAssetPath('assets/images/icons/clinical_icon_file_plans' + (hasWfBadge ? '_cr' : '') + '.svg')
    }

    const fileType = fsi?.type || FsiType.DOCUMENT

    switch (fileType) {
      case FsiType.FOLDER:
        return getAssetPath('assets/images/medidata_folder_icon.svg')

      case FsiType.DOCUMENT:
        const docExtension = extensionFilter(fsi.name)
        return getAssetPath('assets/images/exts/' + (hasWfBadge ? 'cr/' : '') + docExtension + '.png')

      case FsiType.PLACEHOLDER:
        if (fsi.maxDocuments === -1) {
          return getAssetPath('assets/images/exts/' + (hasWfBadge ? 'cr/' : '') + 'placeholder_unlimited.svg')
        }
        return getAssetPath('assets/images/exts/' + (hasWfBadge ? 'cr/' : '') + 'placeholder.png')

      case FsiType.REFERENCE:
        const refExtension = extensionFilter(fsi.name)
        return getAssetPath('assets/images/exts/alias/' + refExtension + '.png')

      default:
        const defaultExtension = extensionFilter(fsi.name)
        return getAssetPath('assets/images/exts/' + (hasWfBadge ? 'cr/' : '') + defaultExtension + '.png')
    }
  }

  const getAltText = (): string => {
    if (fsi.isFilePlan) {
      return 'File plan icon'
    }

    const fileType = fsi?.type || FsiType.DOCUMENT
    if (fileType === FsiType.FOLDER) {
      return 'Folder icon'
    } else if (fsi?.name) {
      const extension = extensionFilter(fsi.name)
      return `${extension.toUpperCase()} file icon`
    }
    return 'Document Icon'
  }

  // Build CSS classes to match JSX version
  const getCssClasses = (): string => {
    let classes = 'thumb-image'

    if (fsi.isFilePlan) {
      classes += ' isfileplan'
    }

    if (hasWfBadge) {
      classes += ' cr'
    }

    if (fsi.type === FsiType.FOLDER) {
      classes += ' isFolder'
    }

    return classes
  }

  return <img src={getIconPath()} alt={getAltText()} className={getCssClasses()} />
}

export default FsiExtImg
